import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Share2, Copy, Check, Users, Gift } from "lucide-react";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState("");
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/users/me/referrals", { headers: authHeaders() });
        setData(r.data);
      } catch (_) {}
    })();
  }, []);

  if (!data) return null;
  const link = `${origin}/signup?ref=${data.code}`;
  const shareText = `Tune in or launch your own internet radio station on Fliboe. Use my code ${data.code} or sign up here: ${link}`;

  const copy = async (key, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); } catch (_) {}
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Fliboe", text: shareText, url: link }); return; } catch (_) {}
    }
    copy("share", shareText);
  };

  return (
    <div className="fl-card p-5 mb-6" data-testid="referral-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
            <Gift className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="min-w-0">
            <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500">// Invite friends</div>
            <div className="fl-display text-lg font-bold leading-tight">Spread the signal</div>
          </div>
        </div>
        <div className="flex items-center gap-2 fl-mono text-[10px] tracking-widest uppercase text-slate-400">
          <Users className="w-3.5 h-3.5 text-emerald-500" />
          <span className="text-emerald-500 font-semibold text-xs" data-testid="referral-count">{data.count}</span>
          joined via you
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-5 gap-3 items-end">
        {/* Code */}
        <div className="sm:col-span-1">
          <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Your code</div>
          <button
            onClick={() => copy("code", data.code)}
            className="w-full text-left fl-input fl-mono text-lg tracking-[0.3em] uppercase text-emerald-500 cursor-pointer hover:border-emerald-500/60 transition flex items-center justify-between"
            data-testid="copy-referral-code"
          >
            {data.code}
            {copied === "code" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-500" />}
          </button>
        </div>

        {/* Link */}
        <div className="sm:col-span-3">
          <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Your link</div>
          <button
            onClick={() => copy("link", link)}
            className="w-full text-left fl-input text-xs cursor-pointer hover:border-emerald-500/60 transition flex items-center justify-between gap-2"
            data-testid="copy-referral-link"
          >
            <span className="truncate fl-mono">{link}</span>
            {copied === "link" ? <Check className="w-4 h-4 text-emerald-400 shrink-0" /> : <Copy className="w-4 h-4 text-slate-500 shrink-0" />}
          </button>
        </div>

        {/* Share */}
        <div className="sm:col-span-1">
          <button onClick={share} className="fl-btn w-full !py-2.5 !px-3 text-sm" data-testid="share-referral">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
      </div>

      {/* Reward progress */}
      {data.rewards && (
        <div className="mt-4 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20" data-testid="referral-rewards">
          <div className="flex items-center justify-between text-xs">
            <span className="fl-mono tracking-widest uppercase text-emerald-500">
              Reward · 1 month Premium per 3 referrals
            </span>
            <span className="fl-mono text-emerald-500">
              {data.rewards.earned_months > 0
                ? `${data.rewards.earned_months} month${data.rewards.earned_months > 1 ? "s" : ""} earned`
                : "0 earned"}
            </span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${((data.count % 3) / 3) * 100 || (data.count > 0 && data.rewards.next_reward_in === 0 ? 100 : 0)}%` }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-400">
            <span>{data.count % 3} / 3 toward next reward</span>
            {data.rewards.premium_until && data.rewards.premium_source === "referral" && (
              <span className="fl-mono text-emerald-400">
                Premium active · until {String(data.rewards.premium_until).slice(0, 10)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Quick share targets */}
      <div className="mt-3 flex flex-wrap gap-2">
        <a target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} className="fl-pill" data-testid="share-whatsapp">WhatsApp</a>
        <a target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`} className="fl-pill" data-testid="share-x">X / Twitter</a>
        <a target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`} className="fl-pill" data-testid="share-facebook">Facebook</a>
        <a target="_blank" rel="noreferrer" href={`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(shareText)}`} className="fl-pill" data-testid="share-telegram">Telegram</a>
        <a target="_blank" rel="noreferrer" href={`mailto:?subject=${encodeURIComponent("Join me on Fliboe")}&body=${encodeURIComponent(shareText)}`} className="fl-pill" data-testid="share-email">Email</a>
      </div>

      {data.count > 0 && (
        <details className="mt-4 group">
          <summary className="cursor-pointer text-xs text-slate-400 hover:text-white fl-mono tracking-widest uppercase">// {data.count} people you brought to Fliboe</summary>
          <div className="mt-3 space-y-1.5">
            {data.referrals.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="truncate">{r.name || r.email_masked}</span>
                <span className="fl-mono text-[10px] tracking-widest uppercase text-slate-500">{r.plan} · {(r.joined_at || "").slice(0,10)}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <p className="mt-4 fl-mono text-[10px] tracking-widest uppercase text-slate-600">// Every 3 sign-ups via your code = 1 month of Premium, on the house.</p>
    </div>
  );
}
